import React, { useState, useEffect, useRef } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FileSignature, Calendar, Euro, ShieldCheck, FileText, Plus, X, Home, RefreshCw, Trash2, Hash, Repeat, Pencil, Download, Receipt, AlertTriangle, TrendingUp, MoreHorizontal, Users, Upload, Send, LogOut, ShieldAlert, Loader2, Copy, Check, ExternalLink, Sparkles, ScanLine, GripVertical, ArrowUpDown } from "lucide-react";
import { EmptyState, openOnboarding } from "../../components/onboarding/EmptyState";
import { EMPTY as EMPTY_TXT, HELP } from "../../content/onboardingTexts";
import { maybeDropDemo } from "../../components/onboarding/EmptyState";
import { LaterSection, HelpTip } from "../../components/onboarding/LaterSection";
import api from "../../lib/api";
import OcrReviewModal from "../../components/OcrReviewModal";

function DossierAnalyzer({ rent, docs = [] }) {
  const [f, setF] = useState({ applicant_name: "", monthly_income: "", contract_type: "CDI", guarantor: false });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const analyze = async (fromDocs) => {
    setBusy(true);
    try {
      const payload = { ...f, monthly_income: Number(f.monthly_income) || 0, rent: Number(rent) || 0 };
      if (fromDocs) payload.document_urls = docs;
      const r = await api.post("/ai/analyze-application", payload);
      setRes(r.data);
      if (fromDocs && r.data.detected_income) setF((c) => ({ ...c, monthly_income: r.data.detected_income, contract_type: r.data.detected_contract || c.contract_type }));
    } catch (e) { setRes(null); }
    finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2 rounded-xl border border-brand-soft outline-none focus:border-brand-lime text-sm bg-white";
  return (
    <div className="rounded-2xl border border-brand-soft bg-brand-softer/50 p-4 mt-2" data-testid="dossier-analyzer">
      <div className="flex items-center gap-2 text-brand-greenDeep font-semibold text-sm mb-3"><Sparkles className="w-4 h-4 text-brand-limeDark" /> Analyse de dossier par IA</div>
      {docs.length > 0 && (
        <button type="button" onClick={() => analyze(true)} disabled={busy} className="w-full mb-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold disabled:opacity-60" data-testid="da-analyze-docs-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Analyser les {docs.length} pièce(s) jointe(s) (OCR)
        </button>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input value={f.applicant_name} onChange={(e) => setF({ ...f, applicant_name: e.target.value })} className={inp} placeholder="Nom du candidat" data-testid="da-name" />
        <input value={f.contract_type} onChange={(e) => setF({ ...f, contract_type: e.target.value })} className={inp} placeholder="Type de contrat (CDI…)" />
        <input type="number" value={f.monthly_income} onChange={(e) => setF({ ...f, monthly_income: e.target.value })} className={inp} placeholder="Revenu mensuel net (€)" data-testid="da-income" />
        <label className="flex items-center gap-2 text-sm text-brand-green/70 px-1"><input type="checkbox" checked={f.guarantor} onChange={(e) => setF({ ...f, guarantor: e.target.checked })} className="w-4 h-4 accent-brand-limeDark" /> Garant</label>
      </div>
      <button type="button" onClick={() => analyze(false)} disabled={busy} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="da-run-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Analyser (saisie manuelle)</button>
      {res && (
        <div className="mt-3 space-y-2" data-testid="da-result">
          {res.analyzed_documents > 0 && <p className="text-xs text-brand-green/50">📄 {res.analyzed_documents} pièce(s) lue(s){res.detected_income ? ` · revenu détecté : ${res.detected_income.toLocaleString("fr-FR")} €` : ""}</p>}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-white rounded-xl px-2 py-2 border border-brand-soft"><p className="text-[11px] text-brand-green/45">Solvabilité</p><p className="font-semibold text-brand-greenDeep text-sm">{res.solvency}</p></div>
            <div className="bg-white rounded-xl px-2 py-2 border border-brand-soft"><p className="text-[11px] text-brand-green/45">Taux d'effort</p><p className="font-semibold text-brand-greenDeep text-sm">{res.effort_rate}%</p></div>
            <div className="bg-white rounded-xl px-2 py-2 border border-brand-soft"><p className="text-[11px] text-brand-green/45">Cohérence</p><p className="font-semibold text-brand-greenDeep text-sm">{res.coherence}</p></div>
          </div>
          {res.fraud_flags?.length > 0 && <div className="rounded-xl bg-red-50 border border-red-200 p-2 text-xs text-red-700"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />{res.fraud_flags.join(" · ")}</div>}
          {res.recommendation && <p className="text-sm text-brand-green/75">{res.recommendation}</p>}
        </div>
      )}
    </div>
  );
}
import { toast } from "sonner";
import { IrlOptions } from "../../components/IrlOptions";
import { LeaseClauses } from "../../components/leases/LeaseClauses";
import { LeaseDocumentsModal } from "../../components/leases/LeaseDocumentsModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function endingSoon(lease) {
  if (lease.renouvellement || !lease.end) return null;
  const active = (lease.etat || (lease.status === "Actif" ? "Active" : "Inactive")) === "Active";
  if (!active) return null;
  const days = Math.ceil((new Date(lease.end) - new Date()) / 86400000);
  if (days < 0) return { days, label: "Bail échu", cls: "bg-red-100 text-red-600" };
  if (days <= 90) return { days, label: `Fin dans ${days} j`, cls: "bg-amber-100 text-amber-700" };
  return null;
}

const TYPES = [
  "Bail d'habitation vide", "Bail d'habitation meublé", "Bail meublé étudiant", "Bail colocation", "Bail mobilité",
  "Bail location saisonnière", "Bail parking / garage", "Bail stockage", "Bail mixte",
  "Bail commercial", "Bail professionnel", "Bail de droit commun", "Bail rural",
];
const UTILISATIONS = [
  "Résidence principale du locataire",
  "Résidence secondaire du locataire",
  "Activité professionnelle (hors activité commerciale, artisanale ou industrielle)",
];
const PERIODICITE = ["Mensuel", "Bimestriel", "Trimestriel", "Quadrimestriel", "Semestriel", "Annuel", "Forfaitaire"];
const MOYENS = ["Carte de crédit", "Chèque", "Espèces", "Prélèvement automatique", "Virement"];
const CHARGES_TYPES = ["Provision pour charges", "Forfait de charges"];
const AUTRES_TYPES = ["Charges locatives", "Loyer"];

const EMPTY = {
  property: "", tenant: "", etat: "Active", type: TYPES[0], utilisation: UTILISATIONS[0],
  reference: "", start: "", end: "", renouvellement: false,
  paiement: "Mensuel", moyen_paiement: "Virement", date_paiement: "", date_quittancement: "",
  generation_loyer: 5, rent: "", charges: "", charges_type: CHARGES_TYPES[0], deposit: "",
  autres_paiements: "", autres_paiements_type: AUTRES_TYPES[0], status: "Actif",
  garants: [], assurance_doc: "", docs_salaires: [], doc_domicile: "", doc_identite: "",
  tenant_income: "", tenant_contract: "", tenant_employer: "",
  clause_model: "", activite: "", clauses: [],
};

const _daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.ceil((d - new Date()) / 86400000);
};

function GarantCheck({ income, rentCC, pieceExpiration }) {
  const inc = Number(income) || 0;
  const effort = inc && rentCC ? Math.round((rentCC / inc) * 100) : null;
  const v = effort === null ? null : effort <= 33 ? { t: "Solide (≥3× loyer)", c: "text-emerald-700 bg-emerald-100" } : effort <= 45 ? { t: "Limite", c: "text-amber-700 bg-amber-100" } : { t: "Insuffisant", c: "text-red-600 bg-red-100" };
  const dd = _daysUntil(pieceExpiration);
  if (effort === null && dd === null) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="garant-check">
      {effort !== null && (
        <span className="inline-flex items-center gap-2 text-xs text-brand-green/70">
          Taux d'effort garant : <span className={`font-bold px-2 py-0.5 rounded-full ${v.c}`}>{effort}% · {v.t}</span>
        </span>
      )}
      {dd !== null && dd < 90 && (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${dd < 0 ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`} data-testid="garant-piece-alert">
          <AlertTriangle className="w-3 h-3" /> {dd < 0 ? "Pièce expirée" : `Pièce expire dans ${dd} j`}
        </span>
      )}
    </div>
  );
}

function computeDossier(o) {
  const rentCC = (Number(o?.rent) || 0) + (Number(o?.charges) || 0);
  const ti = Number(o?.tenant_income) || 0;
  const garants = o?.garants || [];
  const gTotal = garants.reduce((s, g) => s + (Number(g?.income) || 0), 0);
  if (!rentCC || (!ti && !gTotal)) return null;
  const tenantOk = ti > 0 && ti >= 3 * rentCC;
  const garantOk = gTotal > 0 && gTotal >= 3 * rentCC;
  const tenantEffort = ti ? Math.round((rentCC / ti) * 100) : null;
  const expired = garants.filter((g) => { if (!g?.piece_expiration) return false; const d = new Date(g.piece_expiration); return !isNaN(d) && d < new Date(); }).length;
  let status = tenantOk ? "solide" : (garantOk && expired === 0) ? "acceptable" : "renforcer";
  const map = {
    solide: { label: "Dossier solide — accepté", short: "Dossier accepté", tone: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    acceptable: { label: "Dossier acceptable (via garant)", short: "Accepté (garant)", tone: "bg-lime-100 text-lime-800 border-lime-200" },
    renforcer: { label: "Dossier à renforcer", short: "À renforcer", tone: "bg-amber-100 text-amber-700 border-amber-200" },
  };
  return { status, ...map[status], rentCC, ti, gTotal, tenantOk, garantOk, tenantEffort, expired };
}

function dossierIssues(form) {
  const out = [];
  if (!(Number(form.tenant_income) > 0)) out.push("Revenus du locataire non renseignés (solvabilité non calculable)");
  if ((form.docs_salaires || []).length === 0) out.push("Fiches de salaire manquantes (3 recommandées)");
  if (!form.doc_identite) out.push("Copie de la pièce d'identité du locataire manquante");
  const d = computeDossier(form);
  if (d && d.status === "renforcer") out.push("Solvabilité insuffisante : dossier « à renforcer »");
  return out;
}

function DossierVerdict({ form }) {
  const d = computeDossier(form);
  if (!d) return null;
  return (
    <div className={`rounded-2xl border p-4 ${d.tone}`} data-testid="dossier-verdict">
      <div className="flex items-center gap-2 font-bold text-sm"><ShieldCheck className="w-4 h-4" /> {d.label}</div>
      <ul className="mt-2 space-y-1 text-xs">
        <li>• Locataire : {d.ti ? `${d.ti.toLocaleString("fr-FR")} € · taux d'effort ${d.tenantEffort}% ${d.tenantOk ? "(≥ 3× loyer ✔)" : "(< 3× loyer)"}` : "revenus non renseignés"}</li>
        <li>• Garant(s) : {d.gTotal ? `${d.gTotal.toLocaleString("fr-FR")} € ${d.garantOk ? "(≥ 3× loyer ✔)" : "(< 3× loyer)"}` : "aucun garant"}</li>
        {d.expired > 0 && <li className="font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {d.expired} pièce(s) d'identité de garant expirée(s)</li>}
      </ul>
    </div>
  );
}

function durationLabel(start, end) {
  if (!start || !end) return "—";
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return "—";
  let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (e.getDate() < s.getDate()) months -= 1;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts = [];
  if (years) parts.push(`${years} an${years > 1 ? "s" : ""}`);
  if (rem) parts.push(`${rem} mois`);
  return parts.length ? parts.join(" et ") : "< 1 mois";
}

function LeaseReorderRow({ id, property, tenant }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={id} dragListener={false} dragControls={controls} className="flex items-center gap-3 bg-white border border-brand-soft rounded-2xl px-4 py-3">
      <button onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-brand-green/40 hover:text-brand-greenDeep touch-none p-1" data-testid={`lease-drag-${id}`}><GripVertical className="w-4 h-4" /></button>
      <FileSignature className="w-4 h-4 text-brand-green/40 shrink-0" />
      <span className="font-semibold text-brand-greenDeep text-sm flex-1 truncate">{property}</span>
      <span className="text-xs text-brand-green/50 shrink-0 truncate max-w-[160px]">{tenant || "—"}</span>
    </Reorder.Item>
  );
}

export default function Locations() {
  const [leases, setLeases] = useState([]);
  const [showAllLeases, setShowAllLeases] = useState(false);
  const [cardOrder, setCardOrder] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderIds, setReorderIds] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [renewLease, setRenewLease] = useState(null);
  const [terminateLease, setTerminateLease] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [arrearsLease, setArrearsLease] = useState(null);
  const [docsLease, setDocsLease] = useState(null);
  const [soldeSignLease, setSoldeSignLease] = useState(null);
  const [recapLease, setRecapLease] = useState(null);
  const [regulLease, setRegulLease] = useState(null);
  const [forceConfirm, setForceConfirm] = useState(null);
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState(false);
  const load = () => api.get("/leases").then((r) => { setLeases(r.data); setLoaded(true); }).catch(() => {});
  const [syncing, setSyncing] = useState(null);
  const syncLease = async (l) => {
    setMenuOpen(null);
    setSyncing(l.id);
    try {
      const { data } = await api.post(`/leases/${l.id}/sync`);
      let msg = `Synchronisé : ${data.receipts_generated} quittance(s) générée(s) en attente`;
      if (data.deposit_generated) msg += " · dépôt de garantie généré";
      toast.success(msg);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Synchronisation impossible"); }
    finally { setSyncing(null); }
  };
  useEffect(() => {
    load();
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
    api.get("/tenants").then((r) => setTenants(r.data || [])).catch(() => {});
    api.get("/ui/card-order/leases").then((r) => setCardOrder(r.data.order || [])).catch(() => {});
  }, []);

  const [errors, setErrors] = useState({});
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => (e[k] ? { ...e, [k]: false } : e)); };

  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [overrideBlock, setOverrideBlock] = useState(false);
  const [leaseDocs, setLeaseDocs] = useState([]);
  const [leaseOcrBusy, setLeaseOcrBusy] = useState(false);
  const [leaseOcrResult, setLeaseOcrResult] = useState(null);
  const [leaseMissing, setLeaseMissing] = useState(null);
  const [guidedBusy, setGuidedBusy] = useState("");
  const leaseDocRef = useRef(null);

  const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : (d?.errors?.join(" · ") || d?.message || fb); };

  const createMissingProperty = async () => {
    const addr = leaseMissing?.property_address;
    if (!addr) return;
    setGuidedBusy("prop");
    try {
      const name = addr.split(",")[0].trim().slice(0, 60) || "Nouveau bien";
      const payload = { name, address: addr, type: "Appartement", rent: Number(leaseMissing.rent) || 0, charges: Number(leaseMissing.charges) || 0 };
      const res = await api.post("/properties", payload);
      const list = await api.get("/properties"); setProperties(list.data || []);
      setForm((f) => ({ ...f, property: res.data.name }));
      setLeaseMissing((m) => ({ ...m, property_address: "" }));
      toast.success(`Bien « ${res.data.name} » créé et sélectionné`);
    } catch (e) { toast.error(errMsg(e, "Création du bien impossible")); }
    finally { setGuidedBusy(""); }
  };
  const createMissingTenant = async () => {
    const raw = leaseMissing?.tenant_name;
    if (!raw) return;
    setGuidedBusy("tenant");
    try {
      const full = raw.replace(/^\s*(M\. et Mme|Mme|Mlle|M\.)\s+/i, "").trim();
      const parts = full.split(/\s+/);
      const nom = parts.length > 1 ? parts.slice(-1).join(" ") : full;
      const prenom = parts.length > 1 ? parts.slice(0, -1).join(" ") : "";
      const co = leaseMissing.company;
      const lt = leaseMissing.lease_type || form.type || "";
      const usage = /commercial/i.test(lt) ? "commercial" : /professionnel/i.test(lt) ? "professionnel" : /parking|stockage/i.test(lt) ? "parking" : "habitation";
      const payload = co
        ? { name: co.raison_sociale || full, email: "", type_locataire: "Société / Autre", company: { ...co, usage }, draft: true, force: true }
        : { name: full, email: "", prenom, nom };
      const res = await api.post("/tenants", payload);
      const list = await api.get("/tenants"); setTenants(list.data || []);
      setForm((f) => ({ ...f, tenant: res.data.name }));
      setLeaseMissing((m) => ({ ...m, tenant_name: "" }));
      toast.success(`Locataire « ${res.data.name} » créé et sélectionné`);
    } catch (e) { toast.error(errMsg(e, "Création du locataire impossible")); }
    finally { setGuidedBusy(""); }
  };

  const LEASE_OCR_FIELDS = [
    { key: "property", label: "Bien loué" },
    { key: "tenant", label: "Locataire" },
    { key: "type", label: "Type de bail" },
    { key: "rent", label: "Loyer HC (€)" },
    { key: "charges", label: "Charges (€)" },
    { key: "charges_type", label: "Type de charges" },
    { key: "deposit", label: "Dépôt de garantie (€)" },
    { key: "start", label: "Début du bail" },
    { key: "end", label: "Fin du bail" },
    { key: "paiement", label: "Périodicité" },
    { key: "reference", label: "Référence" },
    { key: "index_type", label: "Indice de révision" },
    { key: "tva_rate", label: "TVA (%)" },
    { key: "activite", label: "Activité autorisée" },
    { key: "usage", label: "Usage (locataire société)" },
  ];
  const addLeaseDocs = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setLeaseDocs((d) => [...d, ...files.map((f) => ({ file: f, name: f.name }))]);
    if (leaseDocRef.current) leaseDocRef.current.value = "";
  };
  const removeLeaseDoc = (i) => setLeaseDocs((d) => d.filter((_, idx) => idx !== i));
  const runLeaseOcr = async () => {
    if (!leaseDocs.length) { toast.error("Ajoutez le contrat de bail à analyser"); return; }
    setLeaseOcrBusy(true);
    try {
      const fd = new FormData();
      leaseDocs.forEach((d) => fd.append("files", d.file));
      const { data } = await api.post("/ai/extract-lease-contract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fx = data.fields || {};
      const norm = (s) => (s || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");
      // Résolution du bien et du locataire vers les options existantes
      let matchedProp = "";
      if (fx.property_address) {
        const a = norm(fx.property_address);
        const p = properties.find((x) => a.includes(norm(x.name)) || norm(x.name).includes(a) || (x.address && (a.includes(norm(x.address)) || norm(x.address).includes(a))));
        matchedProp = p?.name || "";
      }
      let matchedTenant = "";
      const co = data.company;
      if (co?.siret) matchedTenant = tenants.find((x) => x.company?.siret === co.siret)?.name || "";
      if (!matchedTenant && fx.tenant_name) {
        const a = norm(fx.tenant_name);
        const t = tenants.find((x) => a.includes(norm(x.name)) || norm(x.name).includes(a));
        matchedTenant = t?.name || "";
      }
      const lt = fx.type || "";
      const detected = { ...fx, property: matchedProp, tenant: matchedTenant,
        ...(co ? { usage: /commercial/i.test(lt) ? "commercial" : /professionnel/i.test(lt) ? "professionnel" : /parking|stockage/i.test(lt) ? "parking" : "habitation" } : {}) };
      let extra = "";
      if (co) extra += ` Locataire société : ${co.raison_sociale}${co.forme_juridique ? ` (${co.forme_juridique})` : ""}${co.siret ? ` — SIRET ${co.siret}` : ""}${data.sirene ? " — vérifiée dans l'annuaire SIRENE" : " — non trouvée dans l'annuaire, à vérifier"}${co.etat && co.etat !== "Active" ? ` — ATTENTION : ${co.etat}` : ""}${co.procedure ? " — procédure collective en cours" : ""}.`;
      if (fx.tenant_name && !matchedTenant) extra += ` Locataire détecté : « ${fx.tenant_name} » (non trouvé, à créer/sélectionner).`;
      if (fx.property_address && !matchedProp) extra += ` Bien détecté : « ${fx.property_address} » (non trouvé, à créer/sélectionner).`;
      setLeaseMissing({
        property_address: (fx.property_address && !matchedProp) ? fx.property_address : "",
        tenant_name: (fx.tenant_name && !matchedTenant) ? fx.tenant_name : "",
        company: co && !matchedTenant ? co : null, lease_type: fx.type,
        rent: fx.rent, charges: fx.charges,
      });
      setLeaseOcrResult({ ...data, fields: detected, summary: (data.summary || "") + extra });
    } catch (e) {
      toast.error(errMsg(e, "Analyse du bail impossible"));
    } finally { setLeaseOcrBusy(false); }
  };
  const applyLeaseOcr = (selected) => {
    setForm((f) => ({ ...f, ...selected }));
    const n = Object.keys(selected).length;
    toast.success(n ? `${n} champ(s) appliqué(s) à la location` : "Aucun champ sélectionné");
  };

  const prefillFromDocs = async () => {
    const docs = [...(form.docs_salaires || []), form.doc_identite, form.doc_domicile].filter(Boolean);
    if (docs.length === 0) { toast.error("Ajoutez d'abord des pièces (fiches de salaire, contrat, pièce d'identité…)"); return; }
    setOcrBusy(true);
    try {
      const { data } = await api.post("/ai/extract-lease-dossier", { document_urls: docs });
      setOcrResult(data);
      setOverrideBlock(false);
      setForm((f) => {
        const next = { ...f };
        const t = data.tenant || {};
        if (t.monthly_income) next.tenant_income = t.monthly_income;
        if (t.contract_type) next.tenant_contract = t.contract_type;
        if (t.employer) next.tenant_employer = t.employer;
        const gExisting = [...(f.garants || [])];
        (data.guarantors || []).forEach((g, i) => {
          const [prenom, ...rest] = (g.name || "").trim().split(" ");
          gExisting[i] = {
            ...(gExisting[i] || {}),
            prenom: prenom || (gExisting[i]?.prenom || ""),
            nom: rest.join(" ") || (gExisting[i]?.nom || ""),
            income: g.monthly_income || gExisting[i]?.income || "",
            contract: g.contract_type || gExisting[i]?.contract || "",
            employer: g.employer || gExisting[i]?.employer || "",
          };
        });
        next.garants = gExisting;
        return next;
      });
      // Pré-remplissage de la fiche locataire depuis la pièce d'identité
      const idn = data.identity || {};
      const hasId = idn.first_name || idn.last_name || idn.birth_date || idn.doc_number;
      const tenantRec = tenants.find((x) => x.name === form.tenant);
      if (hasId && tenantRec) {
        const patch = {
          ...tenantRec,
          civilite: idn.civilite || tenantRec.civilite || "",
          prenom: idn.first_name || tenantRec.prenom || "",
          nom: idn.last_name || tenantRec.nom || "",
          date_naissance: idn.birth_date || tenantRec.date_naissance || "",
          lieu_naissance: idn.birth_place || tenantRec.lieu_naissance || "",
          nationalite: idn.nationality || tenantRec.nationalite || "",
          piece_type: idn.doc_type || tenantRec.piece_type || "",
          piece_numero: idn.doc_number || tenantRec.piece_numero || "",
          piece_expiration: idn.expiry_date || tenantRec.piece_expiration || "",
          revenus_mensuels: (data.tenant?.monthly_income ? String(data.tenant.monthly_income) : tenantRec.revenus_mensuels) || "",
          profession: data.tenant?.employer || tenantRec.profession || "",
        };
        try {
          await api.put(`/tenants/${tenantRec.id}`, patch);
          api.get("/tenants").then((r) => setTenants(r.data || [])).catch(() => {});
          toast.success("Fiche locataire mise à jour (identité + revenus)");
        } catch (e) { /* silencieux */ }
      }
      const wc = (data.warnings || []).length;
      if (wc > 0) toast.warning(`OCR : ${data.analyzed_documents} pièce(s) · ${wc} incohérence(s) détectée(s)`);
      else toast.success(`OCR : ${data.analyzed_documents} pièce(s) lue(s) · confiance ${data.confidence}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'analyse OCR");
    } finally { setOcrBusy(false); }
  };

  const openCreate = () => { setEditId(null); setErrors({}); setForm(EMPTY); setOcrResult(null); setOverrideBlock(false); setLeaseDocs([]); setLeaseOcrResult(null); setLeaseMissing(null); setForceConfirm(null); setOpen(true); };
  const openEdit = (l) => { setEditId(l.id); setErrors({}); setForm({ ...EMPTY, ...l }); setOcrResult(null); setOverrideBlock(false); setLeaseDocs([]); setLeaseOcrResult(null); setLeaseMissing(null); setForceConfirm(null); setOpen(true); };

  const onProperty = (name) => {
    const p = properties.find((x) => x.name === name);
    setForm((f) => ({
      ...f, property: name,
      rent: p?.rent ?? f.rent, charges: p?.charges ?? f.charges,
      tenant: p?.tenant || f.tenant,
    }));
  };

  const rentCC = (Number(form.rent) || 0) + (Number(form.charges) || 0);

  const save = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    const req = {
      property: !form.property,
      start: !form.start,
      rent: form.rent === "" || form.rent === null || form.rent === undefined || Number(form.rent) <= 0,
    };
    if (Object.values(req).some(Boolean)) {
      setErrors(req);
      toast.error("Veuillez renseigner les champs obligatoires (en rouge)");
      return;
    }
    setErrors({});
    if (ocrResult && (ocrResult.blocking?.length > 0) && !overrideBlock) {
      toast.error("Dossier bloqué : levez les réserves graves ou cochez la case pour forcer la validation.");
      return;
    }
    if (!opts.force) {
      const issues = dossierIssues(form);
      if (issues.length) { setForceConfirm(issues); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        rent: Number(form.rent) || 0,
        charges: Number(form.charges) || 0,
        deposit: Number(form.deposit) || 0,
        autres_paiements: Number(form.autres_paiements) || 0,
        generation_loyer: Number(form.generation_loyer) || 0,
        tenant_income: Number(form.tenant_income) || 0,
        garants: (form.garants || []).map((g) => ({ ...g, income: Number(g.income) || 0 })),
      };
      if (opts.force) payload.dossier_incomplet = true;
      if (editId) {
        const res = await api.put(`/leases/${editId}`, payload);
        setLeases(leases.map((l) => (l.id === editId ? res.data : l)));
        toast.success("Bail mis à jour");
      } else {
        const res = await api.post("/leases", payload);
        setLeases([res.data, ...leases]);
        toast.success("Bail créé");
        maybeDropDemo();
      }
      setOpen(false); setForm(EMPTY); setEditId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce bail ?")) return;
    try { await api.delete(`/leases/${id}`); setLeases(leases.filter((l) => l.id !== id)); toast.success("Bail supprimé"); }
    catch { toast.error("Erreur"); }
  };

  const generate = async (l) => {
    try {
      const res = await api.post(`/leases/${l.id}/generate-receipts`);
      const n = res.data.generated;
      toast.success(n > 0 ? `${n} quittance${n > 1 ? "s" : ""} générée${n > 1 ? "s" : ""}` : "Aucune nouvelle quittance à générer");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur de génération"); }
  };

  const downloadPdf = (l) => window.open(`${BACKEND_URL}/api/leases/${l.id}/pdf`, "_blank");

  const inviteAssurance = async (tenant) => {
    if (!tenant) return toast.error("Sélectionnez d'abord un locataire");
    try {
      const res = await api.post("/leases/insurance-invite", { tenant });
      toast.success(`Invitation assurance envoyée à ${res.data.to} (email simulé)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const onRenewed = (updated) => setLeases((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  const leaseActive = (l) => (l.etat || (l.status === "Actif" ? "Active" : "Inactive")) === "Active" || l.status === "Actif";
  const orderIdx = (id) => { const i = cardOrder.indexOf(id); return i === -1 ? 1e9 : i; };
  const orderedLeases = [...leases].sort((a, b) => orderIdx(a.id) - orderIdx(b.id));
  const visibleLeases = orderedLeases.filter((l) => showAllLeases || leaseActive(l));

  const startReorder = () => { setReorderIds(orderedLeases.map((l) => l.id)); setReorderMode(true); };
  const saveReorder = async () => {
    setSavingOrder(true);
    try {
      await api.put("/ui/card-order/leases", { order: reorderIds });
      setCardOrder(reorderIds); setReorderMode(false); toast.success("Ordre des baux enregistré");
    } catch { toast.error("Erreur"); } finally { setSavingOrder(false); }
  };
  const inactiveLeaseCount = leases.filter((l) => !leaseActive(l)).length;

  const arrearsReminder = async (l, level) => {
    window.open(`${BACKEND_URL}/api/leases/${l.id}/arrears-pdf?level=${level}`, "_blank");
    try {
      const res = await api.post(`/leases/${l.id}/arrears-reminder`, { level });
      const labels = { 1: "1er rappel", 2: "2e relance", 3: "mise en demeure" };
      toast.success(res.data.simulated ? `${labels[level]} générée + envoyée à ${res.data.to} (email simulé)` : `${labels[level]} envoyée à ${res.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setArrearsLease(null);
  };

  const insuranceReminder = async (l) => {
    setMenuOpen(null);
    window.open(`${BACKEND_URL}/api/leases/${l.id}/insurance-reminder-pdf`, "_blank");
    try {
      const res = await api.post(`/leases/${l.id}/insurance-reminder`);
      toast.success(res.data.simulated ? `Rappel assurance généré + invitation envoyée à ${res.data.to} (email simulé)` : `Rappel assurance envoyé à ${res.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (loaded && leases.length === 0 && !open) return properties.length
    ? <EmptyState icon={FileSignature} title={EMPTY_TXT.baux.title} cta={EMPTY_TXT.baux.cta} onCta={openCreate} testid="empty-baux" />
    : <EmptyState icon={FileSignature} title={EMPTY_TXT.needProperty} cta={EMPTY_TXT.locatairesNoProp.cta} onCta={() => openOnboarding(1)} testid="empty-baux-noprop" />;
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-brand-green/60">{visibleLeases.length} contrat{visibleLeases.length > 1 ? "s" : ""} de location{!showAllLeases && inactiveLeaseCount > 0 ? ` · ${inactiveLeaseCount} inactive(s) masquée(s)` : ""}</p>
        <div className="flex items-center gap-2">
          <div className="flex p-1 bg-brand-softer rounded-full" data-testid="lease-active-toggle">
            <button onClick={() => setShowAllLeases(false)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${!showAllLeases ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/60"}`} data-testid="lease-filter-active">Actives</button>
            <button onClick={() => setShowAllLeases(true)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${showAllLeases ? "bg-white shadow text-brand-greenDeep" : "text-brand-green/60"}`} data-testid="lease-filter-all">Toutes</button>
          </div>
          {reorderMode ? (
            <>
              <button onClick={() => setReorderMode(false)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-brand-soft text-brand-green/70 font-semibold text-sm transition-colors hover:bg-brand-softer" data-testid="cancel-reorder-btn">Annuler</button>
              <button onClick={saveReorder} disabled={savingOrder} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white font-semibold text-sm transition-colors disabled:opacity-60" data-testid="save-reorder-btn">{savingOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Terminer</button>
            </>
          ) : (
            <button onClick={startReorder} disabled={leases.length < 2} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-brand-soft text-brand-green font-semibold text-sm transition-colors hover:bg-brand-softer disabled:opacity-50" data-testid="reorder-btn">
              <ArrowUpDown className="w-4 h-4" /> Réorganiser
            </button>
          )}
          <button onClick={openCreate} data-testid="new-lease-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors">
            <Plus className="w-4 h-4" /> Nouveau bail
          </button>
        </div>
      </div>

      {reorderMode ? (
        <div className="space-y-3" data-testid="reorder-list">
          <p className="text-sm text-brand-green/55 bg-brand-lime/10 border border-brand-lime/30 rounded-xl px-4 py-3">Glissez les baux par la poignée pour définir leur ordre d'affichage, puis cliquez sur « Terminer ».</p>
          <Reorder.Group axis="y" values={reorderIds} onReorder={setReorderIds} className="space-y-2">
            {reorderIds.map((id) => {
              const l = leases.find((x) => x.id === id);
              if (!l) return null;
              return <LeaseReorderRow key={id} id={id} property={l.property} tenant={l.tenant} />;
            })}
          </Reorder.Group>
        </div>
      ) : (
      <div className="space-y-4">
        {visibleLeases.map((l) => {
          const es = endingSoon(l);
          return (
          <div key={l.id} className="bg-white rounded-2xl p-6 border border-brand-soft hover:shadow-lg transition-all group" data-testid={`lease-card-${l.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex items-center gap-4 lg:w-64">
                <span className="w-12 h-12 rounded-xl bg-brand-soft flex items-center justify-center shrink-0">
                  <FileSignature className="w-6 h-6 text-brand-green" />
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold text-brand-greenDeep truncate">{l.property}</h3>
                  <p className="text-sm text-brand-green/50 truncate">{l.tenant || "—"}</p>
                  {l.reference && <p className="text-xs text-brand-green/40 flex items-center gap-1"><Hash className="w-3 h-3" />{l.reference}</p>}
                </div>
              </div>
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Info icon={FileText} label="Type" value={l.type} />
                <Info icon={Calendar} label="Durée" value={durationLabel(l.start, l.end)} />
                <Info icon={Euro} label="Loyer CC" value={`${((l.rent || 0) + (l.charges || 0)).toLocaleString("fr-FR")} €`} />
                <Info icon={Repeat} label="Paiement" value={l.paiement || "—"} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {es && <span className={`text-xs px-2.5 py-1 rounded-full font-semibold inline-flex items-center gap-1 ${es.cls}`} data-testid={`lease-ending-${l.id}`}><AlertTriangle className="w-3 h-3" /> {es.label}</span>}
                  {(() => { const d = computeDossier(l); return d ? <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${d.tone}`} title={d.label} data-testid={`lease-dossier-${l.id}`}>{d.short}</span> : null; })()}
                  {l.dossier_incomplet && <span className="text-xs px-2.5 py-1 rounded-full font-semibold border border-amber-300 bg-amber-50 text-amber-700 inline-flex items-center gap-1" title="Bail créé sans dossier complet" data-testid={`lease-forced-${l.id}`}><AlertTriangle className="w-3 h-3" /> Créé sans dossier complet</span>}
                  {l.renouvellement && <span className="text-xs px-2.5 py-1 rounded-full bg-brand-softer text-brand-green/70 font-medium inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Renouvelable</span>}
                  <span className={`text-xs px-3 py-1 rounded-full font-semibold ${(l.etat || l.status) === "Active" || l.status === "Actif" ? "bg-brand-lime/30 text-brand-greenDeep" : "bg-brand-soft text-brand-green/60"}`}>{l.etat || (l.status === "Actif" ? "Active" : "Inactive")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="relative">
                    <button onClick={() => setMenuOpen(menuOpen === l.id ? null : l.id)} className="text-brand-green/50 hover:text-brand-greenDeep transition-colors p-1.5 rounded-full hover:bg-brand-softer" data-testid={`lease-menu-${l.id}`}><MoreHorizontal className="w-5 h-5" /></button>
                    {menuOpen === l.id && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(null)} />
                        <div className="absolute right-0 mt-1 w-60 bg-white rounded-xl border border-brand-soft shadow-xl z-50 py-1.5 max-h-[70vh] overflow-y-auto" data-testid={`lease-menu-panel-${l.id}`}>
                          <MenuHeader label="Bail" />
                          <MenuItem icon={RefreshCw} label="Renouvellement de bail" onClick={() => { setMenuOpen(null); setRenewLease(l); }} testid={`renew-lease-${l.id}`} />
                          <MenuItem icon={Pencil} label="Modifier la location" onClick={() => { setMenuOpen(null); openEdit(l); }} testid={`menu-edit-${l.id}`} />
                          <MenuItem icon={FileText} label="Dossier locataire (PDF)" onClick={() => { setMenuOpen(null); window.open(`${BACKEND_URL}/api/leases/${l.id}/dossier-pdf`, "_blank"); }} testid={`menu-dossier-${l.id}`} />
                          <MenuItem icon={Download} label="Imprimer bail en PDF" onClick={() => { setMenuOpen(null); downloadPdf(l); }} testid={`lease-pdf-${l.id}`} />
                          <MenuItem icon={LogOut} label="Terminer la location" onClick={() => { setMenuOpen(null); setTerminateLease(l); }} testid={`menu-terminate-${l.id}`} />
                          {l.termination && <MenuItem icon={FileText} label="Solde de tout compte (PDF)" onClick={() => { setMenuOpen(null); window.open(`${BACKEND_URL}/api/leases/${l.id}/deposit-statement-pdf`, "_blank"); }} testid={`menu-solde-${l.id}`} />}
                          {l.termination && <MenuItem icon={FileSignature} label="Faire signer le solde" onClick={() => { setMenuOpen(null); setSoldeSignLease(l); }} testid={`menu-solde-sign-${l.id}`} />}
                          <div className="my-1 border-t border-brand-soft" />
                          <MenuHeader label="Quittances" />
                          <MenuItem icon={Receipt} label="Générer les quittances" onClick={() => { setMenuOpen(null); generate(l); }} testid={`generate-receipts-${l.id}`} />
                          <MenuItem icon={Receipt} label="Récap annuel (PDF)" onClick={() => { setMenuOpen(null); setRecapLease(l); }} testid={`menu-recap-${l.id}`} />
                          <MenuItem icon={RefreshCw} label={syncing === l.id ? "Synchronisation…" : "Synchroniser & générer les loyers"} onClick={() => syncLease(l)} testid={`menu-sync-${l.id}`} />
                          <MenuItem icon={TrendingUp} label="Régularisation des charges" onClick={() => { setMenuOpen(null); setRegulLease(l); }} testid={`menu-regul-${l.id}`} />
                          <div className="my-1 border-t border-brand-soft" />
                          <MenuHeader label="Rappels" />
                          <MenuItem icon={AlertTriangle} label="Rappel loyer impayé" onClick={() => { setMenuOpen(null); setArrearsLease(l); }} testid={`menu-arrears-${l.id}`} />
                          <MenuItem icon={ShieldAlert} label="Rappel assurance" onClick={() => insuranceReminder(l)} testid={`menu-insurance-${l.id}`} />
                          <MenuItem icon={FileSignature} label="Documents : congé, attestation, avenant…" onClick={() => { setMenuOpen(null); setDocsLease(l); }} testid={`menu-docs-${l.id}`} />
                          <MenuItem icon={FileText} label="Avis d'échéance (mois prochain)" onClick={() => { setMenuOpen(null); window.open(`${BACKEND_URL}/api/leases/${l.id}/avis-echeance-pdf`, "_blank"); }} testid={`menu-avis-${l.id}`} />
                          <div className="my-1 border-t border-brand-soft" />
                          <MenuItem icon={Trash2} label="Supprimer" danger onClick={() => { setMenuOpen(null); remove(l.id); }} testid={`menu-delete-${l.id}`} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        })}
        {visibleLeases.length === 0 && <p className="text-center text-brand-green/50 py-12">{leases.length === 0 ? "Aucun bail. Créez votre premier contrat." : "Aucune location active. Basculez sur « Toutes » pour voir les baux inactifs."}</p>}
      </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lease-modal">
            <div className="flex items-center justify-between p-6 sticky top-0 bg-white border-b border-brand-soft z-10">
              <h3 className="font-display text-2xl font-semibold text-brand-greenDeep">{editId ? "Modifier le bail" : "Nouveau bail"}</h3>
              <button onClick={() => setOpen(false)} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-8">
              {/* Importer un bail existant (OCR) */}
              <Section title="Importer un bail existant (OCR)">
                <p className="text-xs text-brand-green/50 -mt-2">Vous avez déjà un contrat de bail signé ? Importez-le (PDF ou image) : l'IA en lit les conditions (loyer, charges, dépôt, dates, type…) et propose de pré-remplir la location.</p>
                <div className="space-y-2">
                  {leaseDocs.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 bg-brand-softer rounded-xl px-3 py-2" data-testid={`lease-doc-${i}`}>
                      <FileText className="w-4 h-4 text-brand-greenDeep shrink-0" />
                      <span className="text-sm text-brand-greenDeep truncate flex-1">{d.name}</span>
                      <button type="button" onClick={() => removeLeaseDoc(i)} className="text-brand-green/40 hover:text-red-500" data-testid={`lease-doc-remove-${i}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => leaseDocRef.current?.click()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="add-lease-doc-btn"><Upload className="w-4 h-4" /> Importer un bail</button>
                  <button type="button" onClick={runLeaseOcr} disabled={leaseOcrBusy || !leaseDocs.length} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-50" data-testid="ocr-lease-btn">{leaseOcrBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />} Analyser le bail et pré-remplir</button>
                  <input ref={leaseDocRef} type="file" accept=".pdf,image/*" multiple className="hidden" onChange={addLeaseDocs} data-testid="lease-doc-input" />
                </div>
                {leaseMissing && (leaseMissing.property_address || leaseMissing.tenant_name) && (
                  <div className="rounded-2xl border border-brand-lime bg-brand-lime/10 p-4 space-y-2 mt-3" data-testid="guided-create-panel">
                    <p className="text-sm font-semibold text-brand-greenDeep">Éléments du bail absents de votre compte — créez-les en un clic :</p>
                    {leaseMissing.property_address && (
                      <button type="button" onClick={createMissingProperty} disabled={guidedBusy === "prop"} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-50" data-testid="guided-create-property-btn">{guidedBusy === "prop" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Home className="w-4 h-4" />} Créer le bien « {leaseMissing.property_address.split(",")[0]} »</button>
                    )}
                    {leaseMissing.tenant_name && (
                      <button type="button" onClick={createMissingTenant} disabled={guidedBusy === "tenant"} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-50" data-testid="guided-create-tenant-btn">{guidedBusy === "tenant" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} {leaseMissing.company ? `Créer la fiche société « ${leaseMissing.company.raison_sociale || leaseMissing.tenant_name} »` : `Créer le locataire « ${leaseMissing.tenant_name} »`}</button>
                    )}
                  </div>
                )}
              </Section>

              {/* Bien loué */}
              <Section title="Bien loué">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Bien *" error={errors.property}>
                    <select value={form.property} onChange={(e) => onProperty(e.target.value)} className="input" data-testid="lease-property-select">
                      <option value="">— Sélectionner un bien —</option>
                      {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Locataire">
                    <select value={form.tenant} onChange={(e) => set("tenant", e.target.value)} className="input" data-testid="lease-tenant-select">
                      <option value="">— Aucun —</option>
                      {tenants.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                    </select>
                  </Field>
                </div>
              </Section>

              {/* État */}
              <Section title="État de la location">
                <div className="flex gap-3">
                  {["Active", "Inactive"].map((opt) => (
                    <button key={opt} type="button" onClick={() => set("etat", opt)} data-testid={`etat-${opt}`}
                      className={`flex-1 py-2.5 rounded-xl border font-medium text-sm transition-colors ${form.etat === opt ? "bg-brand-lime border-brand-limeDark text-brand-greenDeep" : "border-brand-soft text-brand-green/70 hover:bg-brand-softer"}`}>{opt}</button>
                  ))}
                </div>
              </Section>

              {/* Type */}
              <Section title="Type de location">
                <Field label={<>Type<HelpTip text={HELP.meuble} /></>}>
                  <select value={form.type} onChange={(e) => set("type", e.target.value)} className="input" data-testid="lease-type-select">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </Section>

              {/* Utilisation */}
              <Section later title="Utilisation">
                <div className="space-y-2">
                  {UTILISATIONS.map((u) => (
                    <button key={u} type="button" onClick={() => set("utilisation", u)} data-testid={`utilisation-${u.slice(0, 12)}`}
                      className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-colors ${form.utilisation === u ? "bg-brand-lime/20 border-brand-limeDark text-brand-greenDeep" : "border-brand-soft text-brand-green/70 hover:bg-brand-softer"}`}>{u}</button>
                  ))}
                </div>
              </Section>

              <Section later title="Usage, indice de révision et TVA">
                <div className="grid sm:grid-cols-3 gap-3">
                  <label className="block text-xs font-medium text-brand-green/70">Usage du local
                    <select value={form.usage || ""} onChange={(e) => { const u = e.target.value; setForm((f) => ({ ...f, usage: u, index_type: { habitation: "IRL", commercial: "ILC", professionnel: "ILAT" }[u] || f.index_type || "IRL" })); }} className="input mt-1" data-testid="lease-usage">
                      <option value="">—</option><option value="habitation">Habitation (personne morale)</option><option value="commercial">Commercial / artisanal</option><option value="professionnel">Professionnel / bureaux</option><option value="parking">Parking / box / entrepôt</option>
                    </select>
                  </label>
                  <label className="block text-xs font-medium text-brand-green/70">Indice de révision<HelpTip text={HELP.irl} />
                    <select value={form.index_type || "IRL"} onChange={(e) => set("index_type", e.target.value)} className="input mt-1" data-testid="lease-index-type">
                      <option value="IRL">IRL</option><option value="ILC">ILC</option><option value="ILAT">ILAT</option>
                    </select>
                  </label>
                  <label className="block text-xs font-medium text-brand-green/70">TVA sur le loyer (%)
                    <input type="number" min="0" step="0.1" value={form.tva_rate || ""} onChange={(e) => set("tva_rate", parseFloat(e.target.value) || 0)} placeholder="0 = pas de TVA" className="input mt-1" data-testid="lease-tva-rate" />
                  </label>
                </div>
                {form.tva_rate > 0 && <p className="text-[11px] text-brand-green/50 mt-1">Loyer soumis à TVA : une facture numérotée (HT / TVA / TTC) remplacera la quittance.</p>}
              </Section>

              <Section later title="Clauses types du bail">
                <LeaseClauses form={form} setForm={setForm} />
              </Section>

              {/* Identifiant */}
              <Section later title="Identifiant / Référence">
                <Field label="Identifiant">
                  <input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Référence ou numéro unique (libre)" className="input" data-testid="lease-reference-input" />
                </Field>
              </Section>

              {/* Durée */}
              <Section title="Durée">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Début du bail *" error={errors.start}><input type="date" value={form.start} onChange={(e) => set("start", e.target.value)} className="input" data-testid="lease-start-input" /></Field>
                  <Field label="Fin du bail"><input type="date" value={form.end} onChange={(e) => set("end", e.target.value)} className="input" data-testid="lease-end-input" /></Field>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-brand-softer px-4 py-3">
                  <span className="text-sm text-brand-green/70">Durée du bail</span>
                  <span className="font-semibold text-brand-greenDeep" data-testid="lease-duration">{durationLabel(form.start, form.end)}</span>
                </div>
                <button type="button" onClick={() => set("renouvellement", !form.renouvellement)} data-testid="lease-renouvellement-toggle" className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${form.renouvellement ? "bg-brand-lime/20 border-brand-limeDark" : "border-brand-soft"}`}>
                  <span className="text-sm text-left"><span className="font-medium text-brand-greenDeep">Renouvellement</span><br /><span className="text-xs text-brand-green/50">Génère les loyers après la date de fin du bail</span></span>
                  <span className={`w-11 h-6 rounded-full relative transition-colors ${form.renouvellement ? "bg-brand-limeDark" : "bg-brand-soft"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.renouvellement ? "left-[22px]" : "left-0.5"}`} /></span>
                </button>
              </Section>

              {/* Paiement */}
              <Section title="Paiement">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Périodicité">
                    <select value={form.paiement} onChange={(e) => set("paiement", e.target.value)} className="input" data-testid="lease-paiement-select">
                      {PERIODICITE.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Moyen de paiement">
                    <select value={form.moyen_paiement} onChange={(e) => set("moyen_paiement", e.target.value)} className="input" data-testid="lease-moyen-select">
                      {MOYENS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                </div>
                <p className="text-xs text-brand-green/50 -mt-2">En choisissant le prélèvement automatique, le système générera les loyers comme payés pour vous faire gagner du temps.</p>

                <h5 className="font-medium text-brand-green/80 pt-2">Dates du loyer</h5>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label={<>Date de paiement<HelpTip text={HELP.echeance} /></>}><input type="date" value={form.date_paiement} onChange={(e) => set("date_paiement", e.target.value)} className="input" data-testid="lease-date-paiement-input" /></Field>
                  <Field label="Date de quittancement"><input type="date" value={form.date_quittancement} onChange={(e) => set("date_quittancement", e.target.value)} className="input" data-testid="lease-date-quittancement-input" /></Field>
                  <Field label="Génération du loyer (J-)"><input type="number" min="0" value={form.generation_loyer} onChange={(e) => set("generation_loyer", e.target.value)} className="input" data-testid="lease-generation-input" /></Field>
                </div>

                <h5 className="font-medium text-brand-green/80 pt-2">Loyer</h5>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Loyer hors charges (€) *" error={errors.rent}><input type="number" value={form.rent} onChange={(e) => set("rent", e.target.value)} className="input" data-testid="lease-rent-input" /></Field>
                  <Field label={<>Type de charges<HelpTip text={HELP.charges} /></>}>
                    <select value={form.charges_type} onChange={(e) => set("charges_type", e.target.value)} className="input" data-testid="lease-charges-type-select">
                      {CHARGES_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Charges locatives (€)"><input type="number" value={form.charges} onChange={(e) => set("charges", e.target.value)} className="input" data-testid="lease-charges-input" /></Field>
                <div className="flex items-center justify-between rounded-xl bg-brand-softer px-4 py-3">
                  <span className="text-sm text-brand-green/70">Loyer charges comprises</span>
                  <span className="font-semibold text-brand-greenDeep" data-testid="lease-rent-cc">{rentCC.toLocaleString("fr-FR")} €</span>
                </div>

                <h5 className="font-medium text-brand-green/80 pt-2">Autres paiements</h5>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Montant (€)"><input type="number" value={form.autres_paiements} onChange={(e) => set("autres_paiements", e.target.value)} className="input" data-testid="lease-autres-input" /></Field>
                  <Field label="Affectation">
                    <select value={form.autres_paiements_type} onChange={(e) => set("autres_paiements_type", e.target.value)} className="input" data-testid="lease-autres-type-select">
                      {AUTRES_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label={<>Dépôt de garantie (€)<HelpTip text={HELP.depot} /></>}><input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} className="input" data-testid="lease-deposit-input" /></Field>
              </Section>

              {/* Garants */}
              <Section later title="Garants">
                <div className="space-y-3">
                  {(form.garants || []).map((g, i) => (
                    <div key={i} className="rounded-2xl border border-brand-soft p-3 space-y-2" data-testid={`garant-row-${i}`}>
                      <div className="flex gap-2 items-center">
                        <input value={g.prenom || ""} onChange={(e) => set("garants", form.garants.map((x, j) => j === i ? { ...x, prenom: e.target.value } : x))} placeholder="Prénom" className="input flex-1" data-testid={`garant-prenom-${i}`} />
                        <input value={g.nom || ""} onChange={(e) => set("garants", form.garants.map((x, j) => j === i ? { ...x, nom: e.target.value } : x))} placeholder="Nom" className="input flex-1" data-testid={`garant-nom-${i}`} />
                        <button type="button" onClick={() => set("garants", form.garants.filter((_, j) => j !== i))} className="text-brand-green/40 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <input type="number" value={g.income || ""} onChange={(e) => set("garants", form.garants.map((x, j) => j === i ? { ...x, income: e.target.value } : x))} placeholder="Revenus (€/mois)" className="input" data-testid={`garant-income-${i}`} />
                        <input value={g.contract || ""} onChange={(e) => set("garants", form.garants.map((x, j) => j === i ? { ...x, contract: e.target.value } : x))} placeholder="Contrat (CDI…)" className="input" data-testid={`garant-contract-${i}`} />
                        <input value={g.employer || ""} onChange={(e) => set("garants", form.garants.map((x, j) => j === i ? { ...x, employer: e.target.value } : x))} placeholder="Employeur" className="input" data-testid={`garant-employer-${i}`} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 items-center">
                        <label className="text-xs text-brand-green/55">Expiration pièce d'identité
                          <input type="date" value={g.piece_expiration || ""} onChange={(e) => set("garants", form.garants.map((x, j) => j === i ? { ...x, piece_expiration: e.target.value } : x))} className="input mt-1" data-testid={`garant-piece-expiration-${i}`} />
                        </label>
                      </div>
                      <GarantCheck income={g.income} rentCC={rentCC} pieceExpiration={g.piece_expiration} />
                    </div>
                  ))}
                  <button type="button" onClick={() => set("garants", [...(form.garants || []), { prenom: "", nom: "", income: "", contract: "", employer: "", piece_expiration: "" }])} className="inline-flex items-center gap-2 text-sm font-medium text-brand-greenDeep hover:text-brand-limeDark" data-testid="add-garant-btn"><Plus className="w-4 h-4" /> Ajouter un garant</button>
                </div>
              </Section>

              <DossierVerdict form={form} />

              {/* Assurance */}
              <Section later title="Assurance habitation">
                <Field label="Document d'assurance du locataire">
                  <LeaseFile value={form.assurance_doc} onChange={(u) => set("assurance_doc", u)} testid="assurance-upload" />
                </Field>
                <button type="button" onClick={() => inviteAssurance(form.tenant)} className="inline-flex items-center gap-2 text-sm font-medium text-brand-greenDeep hover:text-brand-limeDark" data-testid="invite-assurance-btn"><Send className="w-4 h-4" /> Inviter le locataire à souscrire une assurance</button>
              </Section>

              {/* Documents requis */}
              <Section later title="Documents requis (dossier locataire)">
                <Field label="3 dernières fiches de salaire (locataire ou garants)">
                  <div className="space-y-2">
                    {(form.docs_salaires || []).map((u, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4 text-brand-limeDark" />
                        <a href={u.startsWith("/api") ? `${BACKEND_URL}${u}` : u} target="_blank" rel="noreferrer" className="text-brand-limeDark hover:underline flex-1 truncate">Fiche {i + 1}</a>
                        <button type="button" onClick={() => set("docs_salaires", form.docs_salaires.filter((_, j) => j !== i))} className="text-brand-green/40 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                    {(form.docs_salaires || []).length < 3 && <LeaseFile value="" onChange={(u) => set("docs_salaires", [...(form.docs_salaires || []), u])} testid="salaire-upload" label="Ajouter une fiche de salaire" />}
                  </div>
                </Field>
                <Field label="Justificatif de domicile"><LeaseFile value={form.doc_domicile} onChange={(u) => set("doc_domicile", u)} testid="domicile-upload" /></Field>
                <Field label="Copie de la pièce d'identité"><LeaseFile value={form.doc_identite} onChange={(u) => set("doc_identite", u)} testid="identite-upload" /></Field>

                {/* Pré-remplissage OCR */}
                <div className="rounded-2xl border border-brand-lime/40 bg-brand-lime/10 p-4 mt-1">
                  <div className="flex items-center gap-2 text-brand-greenDeep font-semibold text-sm mb-1"><ScanLine className="w-4 h-4 text-brand-limeDark" /> Pré-remplissage automatique (OCR)</div>
                  <p className="text-xs text-brand-green/60 mb-3">Analyse les pièces jointes pour remplir les revenus, le contrat et l'employeur du locataire et des garants.</p>
                  <button type="button" onClick={prefillFromDocs} disabled={ocrBusy} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="ocr-prefill-btn">
                    {ocrBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />} Pré-remplir depuis les pièces
                  </button>

                  {ocrResult && typeof ocrResult.reliability_score === "number" && (
                    <div className="mt-3 flex items-center gap-3" data-testid="ocr-score">
                      <div className={`shrink-0 w-14 h-14 rounded-2xl flex flex-col items-center justify-center font-bold ${ocrResult.reliability_score >= 70 ? "bg-emerald-100 text-emerald-700" : ocrResult.reliability_score >= 45 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        <span className="text-lg leading-none">{ocrResult.reliability_score}</span>
                        <span className="text-[9px] font-medium opacity-70">/ 100</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-brand-greenDeep">Score de fiabilité du dossier</p>
                        <p className="text-xs text-brand-green/55">Identité, revenus vs loyer et cohérence · confiance OCR {ocrResult.confidence}</p>
                        <div className="mt-1.5 h-2 rounded-full bg-brand-soft overflow-hidden">
                          <div className={`h-full ${ocrResult.reliability_score >= 70 ? "bg-emerald-500" : ocrResult.reliability_score >= 45 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${ocrResult.reliability_score}%` }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {ocrResult && (ocrResult.warnings?.length > 0) && (
                    <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3" data-testid="ocr-warnings">
                      <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Incohérences détectées</p>
                      <ul className="mt-1.5 space-y-1 text-xs">
                        {ocrResult.warnings.map((w, i) => (
                          <li key={i} className="flex items-start gap-1.5" data-testid={`ocr-warning-${i}`}>
                            <span className={`mt-0.5 shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${w.severity === "grave" ? "bg-red-600 text-white" : w.severity === "moyenne" ? "bg-amber-500 text-white" : "bg-brand-soft text-brand-greenDeep"}`}>{w.severity === "grave" ? "GRAVE" : w.severity === "moyenne" ? "MOYEN" : "MINEUR"}</span>
                            <span className="text-red-700">{w.message}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {ocrResult && ocrResult.warnings?.length === 0 && (
                    <p className="mt-3 text-xs text-emerald-700 flex items-center gap-1.5" data-testid="ocr-coherent"><Check className="w-3.5 h-3.5" /> Dossier cohérent · confiance {ocrResult.confidence}</p>
                  )}

                  {ocrResult && (ocrResult.blocking?.length > 0) && (
                    <label className="mt-3 flex items-start gap-2 rounded-xl bg-red-100/70 border border-red-300 p-3 cursor-pointer" data-testid="ocr-block-override">
                      <input type="checkbox" checked={overrideBlock} onChange={(e) => setOverrideBlock(e.target.checked)} className="mt-0.5 w-4 h-4 accent-red-600" data-testid="ocr-override-checkbox" />
                      <span className="text-xs text-red-800"><b>Validation bloquée</b> : {ocrResult.blocking.length} réserve(s) grave(s). Cochez pour confirmer que vous les avez levées et forcer la création du bail.</span>
                    </label>
                  )}

                  {ocrResult && ocrResult.identity && (ocrResult.identity.last_name || ocrResult.identity.doc_number || ocrResult.identity.birth_date) && (
                    <div className="mt-3 rounded-xl bg-white border border-brand-soft p-3" data-testid="ocr-identity">
                      <p className="text-xs font-semibold text-brand-greenDeep mb-1.5">Pièce d'identité lue</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-brand-green/70">
                        {(ocrResult.identity.first_name || ocrResult.identity.last_name) && <span><b className="text-brand-greenDeep">Nom :</b> {`${ocrResult.identity.civilite || ""} ${ocrResult.identity.first_name || ""} ${ocrResult.identity.last_name || ""}`.trim()}</span>}
                        {ocrResult.identity.birth_date && <span><b className="text-brand-greenDeep">Né(e) le :</b> {ocrResult.identity.birth_date}{ocrResult.identity.birth_place ? ` à ${ocrResult.identity.birth_place}` : ""}</span>}
                        {ocrResult.identity.nationality && <span><b className="text-brand-greenDeep">Nationalité :</b> {ocrResult.identity.nationality}</span>}
                        {ocrResult.identity.doc_type && <span><b className="text-brand-greenDeep">Type :</b> {ocrResult.identity.doc_type}</span>}
                        {ocrResult.identity.doc_number && <span><b className="text-brand-greenDeep">N° :</b> {ocrResult.identity.doc_number}</span>}
                        {ocrResult.identity.expiry_date && <span className={ocrResult.identity.expired ? "text-red-600 font-medium" : ""}><b className="text-brand-greenDeep">Validité :</b> {ocrResult.identity.expiry_date}{ocrResult.identity.expired ? " (expirée)" : ""}</span>}
                      </div>
                      {!tenants.find((x) => x.name === form.tenant) && <p className="mt-2 text-[11px] text-amber-700">Sélectionnez un locataire ci-dessus pour appliquer ces informations à sa fiche.</p>}
                    </div>
                  )}
                </div>

                {/* Situation du locataire */}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <Field label="Revenus locataire (€/mois)"><input type="number" value={form.tenant_income} onChange={(e) => set("tenant_income", e.target.value)} className="input" data-testid="tenant-income-input" /></Field>
                  <Field label="Type de contrat"><input value={form.tenant_contract} onChange={(e) => set("tenant_contract", e.target.value)} placeholder="CDI, CDD…" className="input" data-testid="tenant-contract-input" /></Field>
                  <Field label="Employeur"><input value={form.tenant_employer} onChange={(e) => set("tenant_employer", e.target.value)} placeholder="Nom de l'employeur" className="input" data-testid="tenant-employer-input" /></Field>
                </div>

                <DossierAnalyzer rent={form.rent} docs={[...(form.docs_salaires || []), form.doc_identite, form.doc_domicile].filter(Boolean)} />
              </Section>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-green font-semibold">Annuler</button>
                <button type="submit" disabled={saving || (ocrResult && ocrResult.blocking?.length > 0 && !overrideBlock)} className="flex-1 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors disabled:opacity-60" data-testid="save-lease-btn">{saving ? "..." : (ocrResult && ocrResult.blocking?.length > 0 && !overrideBlock) ? "Dossier bloqué" : (editId ? "Enregistrer" : "Créer le bail")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {renewLease && <RenewAssistant lease={renewLease} onClose={() => setRenewLease(null)} onDone={onRenewed} />}
      {terminateLease && <TerminateModal lease={terminateLease} onClose={() => setTerminateLease(null)} onDone={onRenewed} />}
      {docsLease && <LeaseDocumentsModal lease={docsLease} onClose={() => setDocsLease(null)} />}
      {arrearsLease && <ArrearsModal lease={arrearsLease} onClose={() => setArrearsLease(null)} onSend={arrearsReminder} />}
      {soldeSignLease && <SoldeSignModal lease={soldeSignLease} onClose={() => setSoldeSignLease(null)} />}
      {recapLease && <RecapModal lease={recapLease} onClose={() => setRecapLease(null)} />}
      {regulLease && <RegulModal lease={regulLease} onClose={() => setRegulLease(null)} />}
      {forceConfirm && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setForceConfirm(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="force-lease-modal">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center"><AlertTriangle className="w-5 h-5" /></div>
              <h3 className="font-display text-lg font-semibold text-brand-greenDeep">Dossier incomplet</h3>
            </div>
            <p className="text-sm text-brand-green/70">Les éléments suivants sont manquants ou insuffisants pour établir la solvabilité et l'acceptation du dossier :</p>
            <ul className="mt-2 mb-3 space-y-1 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl p-3">
              {forceConfirm.map((it, i) => <li key={i} className="flex items-start gap-2"><span className="mt-0.5">•</span>{it}</li>)}
            </ul>
            <p className="text-sm font-medium text-brand-greenDeep">Voulez-vous tout de même <strong>forcer la création du bail</strong> ?</p>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setForceConfirm(null)} className="flex-1 py-2.5 rounded-full border border-brand-soft text-brand-green font-semibold text-sm" data-testid="force-lease-cancel">Annuler</button>
              <button onClick={() => { setForceConfirm(null); save(null, { force: true }); }} className="flex-1 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm inline-flex items-center justify-center gap-2" data-testid="force-lease-confirm"><Check className="w-4 h-4" /> Forcer la création</button>
            </div>
          </div>
        </div>
      )}
      <OcrReviewModal
        open={!!leaseOcrResult}
        onClose={() => setLeaseOcrResult(null)}
        detected={leaseOcrResult?.fields || {}}
        current={form}
        fields={LEASE_OCR_FIELDS}
        confidence={leaseOcrResult?.confidence}
        summary={leaseOcrResult?.summary}
        onApply={applyLeaseOcr}
      />
      <style>{`.input{width:100%;padding:0.7rem 1rem;border-radius:1rem;border:1px solid #efe7d6;outline:none;transition:all .2s;background:#fff}.input:focus{border-color:#c9a24b;box-shadow:0 0 0 3px rgba(201,162,75,.25)}.field-error .input{border-color:#ef4444!important;box-shadow:0 0 0 3px rgba(239,68,68,.15)}`}</style>
    </div>
  );
}

function Section({ title, children, later }) {
  if (later) return <LaterSection title={title}>{children}</LaterSection>;
  return (
    <div>
      <h4 className="font-semibold text-brand-greenDeep border-b border-brand-soft pb-2 mb-4">{title}</h4>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children, error }) {
  return (
    <label className={`block ${error ? "field-error" : ""}`}>
      <span className={`block text-sm font-medium mb-1.5 ${error ? "text-red-600" : "text-brand-green/70"}`}>{label}</span>
      {children}
      {error && <span className="block text-xs text-red-600 mt-1">Ce champ obligatoire est requis</span>}
    </label>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 text-xs text-brand-green/50 mb-1"><Icon className="w-3.5 h-3.5" /> {label}</p>
      <p className="font-semibold text-brand-greenDeep text-sm truncate">{value}</p>
    </div>
  );
}

function MenuHeader({ label }) {
  return <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-brand-green/40">{label}</p>;
}

function MenuItem({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left transition-colors ${danger ? "text-red-600 hover:bg-red-50" : "text-brand-greenDeep hover:bg-brand-softer"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function ModalShell({ title, icon: Icon, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><Icon className="w-5 h-5" /> {title}</h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 pt-5">{children}</div>
      </div>
    </div>
  );
}

const ARREARS_OPTIONS = [
  { level: 1, label: "1er rappel", desc: "Courrier courtois de rappel de paiement.", cls: "border-brand-soft hover:bg-brand-softer" },
  { level: 2, label: "2e relance", desc: "Ton plus ferme, délai de 15 jours.", cls: "border-amber-200 hover:bg-amber-50" },
  { level: 3, label: "Mise en demeure", desc: "Formel, délai 8 jours, mention de procédure.", cls: "border-red-200 hover:bg-red-50" },
];

function ArrearsModal({ lease, onClose, onSend }) {
  const [busy, setBusy] = useState(null);
  return (
    <ModalShell title="Rappel loyer impayé" icon={AlertTriangle} onClose={onClose} testid="arrears-modal">
      <p className="text-sm text-brand-green/60 mb-4">Choisissez le niveau de relance pour <b>{lease.tenant || "le locataire"}</b> — {lease.property}.</p>
      <div className="space-y-3">
        {ARREARS_OPTIONS.map((o) => (
          <button key={o.level} disabled={busy !== null}
            onClick={async () => { setBusy(o.level); await onSend(lease, o.level); }}
            className={`w-full text-left p-4 rounded-2xl border-2 transition-colors disabled:opacity-50 ${o.cls}`}
            data-testid={`arrears-level-${o.level}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-brand-greenDeep flex items-center gap-2">
                {busy === o.level && <Loader2 className="w-4 h-4 animate-spin" />} {o.label}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand-softer text-brand-green/60">Niveau {o.level}</span>
            </div>
            <p className="text-xs text-brand-green/50 mt-1">{o.desc}</p>
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span className="text-brand-green/50">PDF seul (sans envoi) :</span>
        {ARREARS_OPTIONS.map((o) => (
          <button key={o.level} type="button" onClick={() => window.open(`${BACKEND_URL}/api/leases/${lease.id}/arrears-pdf?level=${o.level}`, "_blank")} className="px-2 py-0.5 rounded-full border border-brand-soft hover:bg-brand-softer" data-testid={`arrears-pdf-only-${o.level}`}>Niveau {o.level}</button>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-brand-green/50">La mise en demeure doit être envoyée en recommandé avec AR : imprimez le PDF et postez-le.</p>
      <div className="mt-3 flex flex-wrap gap-4 text-xs">
        <a href="/blog/gerer-un-impaye-de-loyer" target="_blank" rel="noreferrer" className="text-brand-limeDark hover:underline" data-testid="arrears-blog-link">Guide : gérer un impayé de loyer →</a>
        <a href="/app/modeles-documents?kind=relance_1" target="_blank" rel="noreferrer" className="text-brand-limeDark hover:underline" data-testid="arrears-template-link">Personnaliser les courriers →</a>
      </div>
    </ModalShell>
  );
}

function SoldeSignModal({ lease, onClose }) {
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/leases/${lease.id}/solde-signature`);
      setLink(`${window.location.origin}${res.data.url}`);
      toast.success("Lien de signature du solde créé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <ModalShell title="Faire signer le solde" icon={FileSignature} onClose={onClose} testid="solde-sign-modal">
      <p className="text-sm text-brand-green/60 mb-4">Générez un lien de signature en ligne du solde de tout compte pour <b>{lease.tenant || "le locataire"}</b>. Le document sera horodaté et scellé.</p>
      {!link ? (
        <button onClick={create} disabled={busy} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="create-solde-link-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Générer le lien de signature
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input readOnly value={link} className="flex-1 px-3 py-2 rounded-xl border border-brand-soft text-sm bg-brand-softer" data-testid="solde-link-input" />
            <button onClick={copy} className="px-3 rounded-xl border border-brand-soft hover:bg-brand-softer" data-testid="copy-solde-link-btn">{copied ? <Check className="w-4 h-4 text-brand-limeDark" /> : <Copy className="w-4 h-4" />}</button>
          </div>
          <a href={link} target="_blank" rel="noreferrer" className="w-full py-2.5 rounded-full bg-brand-greenDeep text-brand-lime font-semibold flex items-center justify-center gap-2" data-testid="open-solde-sign-btn">
            <ExternalLink className="w-4 h-4" /> Ouvrir la page de signature
          </a>
        </div>
      )}
    </ModalShell>
  );
}

function RecapModal({ lease, onClose }) {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const years = [now, now - 1, now - 2, now - 3];
  return (
    <ModalShell title="Récapitulatif annuel" icon={Receipt} onClose={onClose} testid="recap-modal">
      <p className="text-sm text-brand-green/60 mb-4">Générez un récapitulatif annuel des loyers et charges pour <b>{lease.tenant || "le locataire"}</b>.</p>
      <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Année</label>
      <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="input mb-5" data-testid="recap-year-select">
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <button onClick={() => { window.open(`${BACKEND_URL}/api/leases/${lease.id}/recap-annuel-pdf?year=${year}`, "_blank"); onClose(); }} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2" data-testid="download-recap-btn">
        <Download className="w-4 h-4" /> Télécharger le récap {year}
      </button>
    </ModalShell>
  );
}

function RegulModal({ lease, onClose }) {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  const years = [now, now - 1, now - 2, now - 3];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [postes, setPostes] = useState({});
  const [sending, setSending] = useState(false);

  const load = async (y) => {
    setLoading(true); setData(null); setSelected({}); setPostes({});
    try {
      const res = await api.get(`/leases/${lease.id}/charge-regularization`, { params: { year: y } });
      setData(res.data);
      const sel = {}, pos = {};
      (res.data.expenses || []).forEach((e) => { sel[e.id] = true; pos[e.id] = e.poste || "Autre"; });
      setSelected(sel); setPostes(pos);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

  const provisions = data?.provisions_encaissees || 0;
  const posteList = data?.postes || [];
  const totalRecoverable = (data?.expenses || []).filter((e) => selected[e.id]).reduce((s, e) => s + (e.amount || 0), 0);
  const solde = Math.round((provisions - totalRecoverable) * 100) / 100;
  const fmt = (n) => (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const items = () => Object.keys(selected).filter((k) => selected[k]).map((id) => ({ id, poste: postes[id] || "Autre" }));

  const genPdf = () => {
    api.post(`/leases/${lease.id}/charge-regularization-pdf`, { year, items: items() }, { responseType: "blob" })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a"); a.href = url; a.download = `regularisation-charges-${year}.pdf`;
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
        toast.success("Décompte généré");
      })
      .catch(() => toast.error("Génération impossible"));
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const res = await api.post(`/leases/${lease.id}/charge-regularization-send`, { year, items: items() });
      toast.success(res.data.simulated ? `Décompte envoyé à ${res.data.to} (email simulé)` : `Décompte envoyé à ${res.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="regul-modal">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Régularisation des charges<HelpTip text={HELP.regul} /></h3>
          <button onClick={onClose} className="text-brand-green/40 hover:text-brand-greenDeep"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 pt-5 space-y-4">
          <p className="text-sm text-brand-green/60">Comparez les provisions encaissées et les charges réelles récupérables pour <b>{lease.tenant || "le locataire"}</b>, attribuez un poste à chaque charge, puis générez ou envoyez le décompte.</p>
          <div>
            <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Année</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="input" data-testid="regul-year-select">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div className="rounded-2xl bg-brand-softer p-4 text-sm">
            <div className="flex justify-between"><span className="text-brand-green/60">Provisions encaissées {year}</span><b data-testid="regul-provisions">{fmt(provisions)} €</b></div>
          </div>

          <div>
            <p className="text-sm font-medium text-brand-green/70 mb-2">Dépenses du bien — cochez les charges récupérables et choisissez le poste</p>
            {loading ? (
              <p className="text-sm text-brand-green/50 py-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /> Chargement…</p>
            ) : (data?.expenses || []).length === 0 ? (
              <p className="text-sm text-brand-green/45 py-3 text-center bg-brand-softer/50 rounded-xl" data-testid="regul-expenses-empty">Aucune dépense enregistrée pour ce bien en {year}. Ajoutez-en depuis Finances.</p>
            ) : (
              <div className="space-y-2" data-testid="regul-expenses">
                {data.expenses.map((e) => (
                  <div key={e.id} className="p-3 rounded-xl border border-brand-soft" data-testid={`regul-exp-${e.id}`}>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" checked={!!selected[e.id]} onChange={(ev) => setSelected((s) => ({ ...s, [e.id]: ev.target.checked }))} className="w-4 h-4 accent-brand-limeDark" data-testid={`regul-check-${e.id}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-brand-greenDeep truncate">{e.label || e.category}</p>
                        <p className="text-xs text-brand-green/50">{e.category} · {e.date}</p>
                      </div>
                      <span className="text-sm font-semibold text-brand-greenDeep">{fmt(e.amount)} €</span>
                    </label>
                    {selected[e.id] && (
                      <select value={postes[e.id] || "Autre"} onChange={(ev) => setPostes((p) => ({ ...p, [e.id]: ev.target.value }))} className="input mt-2 text-sm" data-testid={`regul-poste-${e.id}`}>
                        {posteList.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-brand-greenDeep text-white p-4 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-white/70">Charges réelles récupérables</span><b>{fmt(totalRecoverable)} €</b></div>
            <div className="flex justify-between border-t border-white/15 pt-2 mt-1">
              <span className="text-white/70">{solde >= 0 ? "À rembourser au locataire" : "Complément dû par le locataire"}</span>
              <b className="text-brand-lime" data-testid="regul-solde">{fmt(Math.abs(solde))} €</b>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={genPdf} disabled={loading} className="flex-1 py-3 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="regul-generate-btn">
              <Download className="w-4 h-4" /> Générer le PDF
            </button>
            <button onClick={sendEmail} disabled={loading || sending} className="flex-1 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="regul-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer au locataire
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeaseFile({ value, onChange, testid, label = "Ajouter un fichier (image ou PDF)" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/tenants/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("Fichier ajouté");
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec de l'upload"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={() => ref.current?.click()} className="w-full py-3 rounded-xl border border-dashed border-brand-soft flex items-center justify-center gap-2 text-sm text-brand-green/60 hover:border-brand-limeDark transition-colors" data-testid={`${testid}-btn`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : value ? <FileText className="w-4 h-4 text-brand-limeDark" /> : <Upload className="w-4 h-4" />}
        {value ? "Fichier ajouté — remplacer" : label}
      </button>
    </div>
  );
}

function daysBetween(a, b) {
  const d1 = new Date(a), d2 = new Date(b);
  if (isNaN(d1) || isNaN(d2)) return 0;
  return Math.round((d2 - d1) / 86400000);
}

function TerminateModal({ lease, onClose, onDone }) {
  const [f, setF] = useState({
    date_depart: lease.end || "", nouvelle_adresse: "", commentaires: "",
    depot_restituer: lease.deposit || "", date_restitution: "", message: "",
    derniere_quittance_partielle: false, date_fin: lease.end || "",
    loyer_hc: lease.rent || 0, charges: lease.charges || 0, retenues: [],
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const deposit = Number(lease.deposit) || 0;
  const retenuesTotal = (f.retenues || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const setRetenues = (rows) => setF((p) => ({ ...p, retenues: rows, depot_restituer: Math.max(0, deposit - rows.reduce((s, r) => s + (Number(r.amount) || 0), 0)) }));

  // prorata: if partial, compute loyer_hc based on days from 1st of month to date_fin
  const computeProrata = () => {
    if (!f.date_fin) return toast.error("Renseignez la date de fin");
    const d = new Date(f.date_fin);
    if (isNaN(d)) return;
    const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const dayOfMonth = d.getDate();
    const full = Number(lease.rent) || 0;
    const prorata = Math.round((full * dayOfMonth / daysInMonth) * 100) / 100;
    set("loyer_hc", prorata);
    toast.success(`Prorata : ${dayOfMonth}/${daysInMonth} jours → ${prorata.toLocaleString("fr-FR")} €`);
  };

  const submit = async () => {
    if (!f.date_depart) return toast.error("Renseignez la date de départ");
    setBusy(true);
    try {
      const payload = {
        ...f,
        depot_restituer: Number(f.depot_restituer) || 0,
        loyer_hc: Number(f.loyer_hc) || 0,
        charges: Number(f.charges) || 0,
      };
      const res = await api.post(`/leases/${lease.id}/terminate`, payload);
      onDone(res.data.lease);
      const c = res.data.created;
      let msg = "Location terminée";
      if (c.expense) msg += " · dépense dépôt créée dans Finances";
      if (c.receipt) msg += " · dernière quittance générée";
      toast.success(msg);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="terminate-modal">
        <div className="flex items-center justify-between p-6 border-b border-brand-soft sticky top-0 bg-white">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><LogOut className="w-5 h-5" /> Terminer la location</h3>
          <button onClick={onClose} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-brand-green/60">{lease.property} · {lease.tenant || "—"}</p>
          <label className="block"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Date du départ</span><input type="date" value={f.date_depart} onChange={(e) => set("date_depart", e.target.value)} className="input" data-testid="term-date-depart" /></label>
          <label className="block"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Nouvelle adresse (correspondance)</span><input value={f.nouvelle_adresse} onChange={(e) => set("nouvelle_adresse", e.target.value)} placeholder="Adresse du locataire après son départ" className="input" data-testid="term-adresse" /></label>
          <label className="block"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Commentaires (relevé des compteurs, observations)</span><textarea rows={3} value={f.commentaires} onChange={(e) => set("commentaires", e.target.value)} className="input resize-none" data-testid="term-commentaires" /></label>

          <div className="border-t border-brand-soft pt-4">
            <h4 className="font-semibold text-brand-greenDeep mb-3">Dépôt de garantie</h4>
            {deposit > 0 && <p className="text-xs text-brand-green/50 mb-3">Dépôt initial : <b>{deposit.toLocaleString("fr-FR")} €</b></p>}

            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-brand-green/70">Retenues (dégradations, impayés)</span>
                <button type="button" onClick={() => setRetenues([...(f.retenues || []), { label: "", amount: "", justificatif: "" }])} className="text-xs font-medium text-brand-greenDeep hover:text-brand-limeDark inline-flex items-center gap-1" data-testid="add-retenue-btn"><Plus className="w-3.5 h-3.5" /> Ajouter une retenue</button>
              </div>
              <div className="space-y-2">
                {(f.retenues || []).map((r, i) => (
                  <div key={i} className="rounded-xl border border-brand-soft p-2.5 space-y-2" data-testid={`retenue-row-${i}`}>
                    <div className="flex gap-2">
                      <input value={r.label} onChange={(e) => setRetenues(f.retenues.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Motif (ex. peinture, mur abîmé)" className="input flex-1" data-testid={`retenue-label-${i}`} />
                      <input type="number" value={r.amount} onChange={(e) => setRetenues(f.retenues.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} placeholder="€" className="input w-24" data-testid={`retenue-amount-${i}`} />
                      <button type="button" onClick={() => setRetenues(f.retenues.filter((_, j) => j !== i))} className="text-brand-green/40 hover:text-red-500 p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <LeaseFile value={r.justificatif} onChange={(u) => setRetenues(f.retenues.map((x, j) => j === i ? { ...x, justificatif: u } : x))} testid={`retenue-file-${i}`} label="Justificatif (photo, devis...)" />
                  </div>
                ))}
              </div>
              {retenuesTotal > 0 && <p className="text-xs text-amber-700 font-medium mt-2" data-testid="retenues-total">Total des retenues : {retenuesTotal.toLocaleString("fr-FR")} €</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Dépôt à restituer (€)</span><input type="number" value={f.depot_restituer} onChange={(e) => set("depot_restituer", e.target.value)} className="input" data-testid="term-depot" /></label>
              <label className="block"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Date de restitution</span><input type="date" value={f.date_restitution} onChange={(e) => set("date_restitution", e.target.value)} className="input" data-testid="term-date-restitution" /></label>
            </div>
            <p className="text-xs text-brand-green/50 mt-1.5">Une dépense « Dépôt de garantie » sera générée dans Finances à la date de restitution.</p>
            <label className="block mt-3"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Message (sur le document)</span><input value={f.message} onChange={(e) => set("message", e.target.value)} className="input" data-testid="term-message" /></label>
          </div>

          <div className="border-t border-brand-soft pt-4">
            <h4 className="font-semibold text-brand-greenDeep mb-3">Dernière quittance</h4>
            <button type="button" onClick={() => set("derniere_quittance_partielle", !f.derniere_quittance_partielle)} className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border transition-colors mb-3 ${f.derniere_quittance_partielle ? "bg-brand-lime/20 border-brand-limeDark" : "border-brand-soft"}`} data-testid="term-partielle-toggle">
              <span className="text-sm text-left text-brand-green/80">La dernière quittance n'est pas d'un mois complet</span>
              <span className={`w-11 h-6 rounded-full relative transition-colors ${f.derniere_quittance_partielle ? "bg-brand-limeDark" : "bg-brand-soft"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${f.derniere_quittance_partielle ? "left-[22px]" : "left-0.5"}`} /></span>
            </button>
            <div className="flex items-end gap-2">
              <label className="block flex-1"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Date de fin</span><input type="date" value={f.date_fin} onChange={(e) => set("date_fin", e.target.value)} className="input" data-testid="term-date-fin" /></label>
              {f.derniere_quittance_partielle && <button type="button" onClick={computeProrata} className="px-3 py-2.5 rounded-xl bg-brand-soft text-brand-greenDeep text-sm font-medium hover:bg-brand-mint" data-testid="term-compute-btn">Calculer</button>}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="block"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Loyer HC (€)</span><input type="number" value={f.loyer_hc} onChange={(e) => set("loyer_hc", e.target.value)} className="input" data-testid="term-loyer" /></label>
              <label className="block"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Charges locatives (€)</span><input type="number" value={f.charges} onChange={(e) => set("charges", e.target.value)} className="input" data-testid="term-charges" /></label>
            </div>
          </div>

          <button onClick={submit} disabled={busy} className="w-full py-3 rounded-full bg-brand-greenDeep text-brand-lime font-semibold transition-opacity hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="term-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />} Terminer la location
          </button>
        </div>
      </div>
    </div>
  );
}

function addMonths(dateStr, months) {
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function RenewAssistant({ lease, onClose, onDone }) {
  const durMonths = (() => {
    if (!lease.start || !lease.end) return 12;
    const s = new Date(lease.start), e = new Date(lease.end);
    let m = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    return m > 0 ? m : 12;
  })();
  const baseEnd = lease.end || new Date().toISOString().slice(0, 10);
  const [newEnd, setNewEnd] = useState(addMonths(baseEnd, durMonths));
  const [keepStart, setKeepStart] = useState(true);
  const [oldIrl, setOldIrl] = useState("");
  const [newIrl, setNewIrl] = useState("");
  const [irlMeta, setIrlMeta] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchIdx = (kind) => (kind === "IRL" ? api.get("/irl/index").then((r) => r.data)
    : api.get(`/indices/${kind}`).then((r) => ({ table: r.data.table, latest: r.data.table[0], year_ago: r.data.table[4] || r.data.table[r.data.table.length - 1] })));
  const loadIrl = () => fetchIdx((lease.index_type || "IRL").toUpperCase()).then((d) => {
    setIrlMeta(d);
    setOldIrl(String(d.year_ago.value));
    setNewIrl(String(d.latest.value));
  }).catch(() => {});
  const idxKind = (lease.index_type || "IRL").toUpperCase();
  useEffect(() => {
    const req = idxKind === "IRL" ? api.get("/irl/index").then((r) => r.data)
      : api.get(`/indices/${idxKind}`).then((r) => ({ table: r.data.table, latest: r.data.table[0], year_ago: r.data.table[4] || r.data.table[r.data.table.length - 1] }));
    req.then((d) => {
      setIrlMeta(d);
      setOldIrl((v) => v || String(d.year_ago.value));
      setNewIrl((v) => v || String(d.latest.value));
    }).catch(() => {});
  }, [idxKind]);

  const refreshIrl = async () => {
    try {

      if (idxKind === "IRL") await api.post("/irl/refresh").catch(() => {}); await loadIrl();
      toast.success(`Indices ${idxKind} actualisés depuis l'INSEE`);
    } catch (e) { toast.error("Actualisation INSEE indisponible"); }
  };

  const history = lease.rent_history || [];

  const rent = Number(lease.rent) || 0;
  const irlValid = Number(oldIrl) > 0 && Number(newIrl) > 0;
  const revisedRent = irlValid ? Math.round(rent * Number(newIrl) / Number(oldIrl) * 100) / 100 : null;
  const increasePct = irlValid ? Math.round((Number(newIrl) - Number(oldIrl)) / Number(oldIrl) * 10000) / 100 : null;

  const applyIrl = async () => {
    if (!irlValid) return toast.error(`Saisissez les deux indices ${idxKind}.`);
    setBusy(true);
    try {
      await api.post(`/leases/${lease.id}/revise-irl`, { old_irl: Number(oldIrl), new_irl: Number(newIrl), apply: true });
      const res = await api.get("/leases");
      const updated = (res.data || []).find((l) => l.id === lease.id);
      if (updated) onDone(updated);
      toast.success(`Loyer révisé à ${revisedRent.toLocaleString("fr-FR")} €`, { action: { label: "Courrier au locataire", onClick: () => window.open(`${BACKEND_URL}/api/leases/${lease.id}/revision-letter-pdf`, "_blank") }, duration: 10000 });
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const renew = async () => {
    if (!newEnd) return toast.error("Choisissez une nouvelle date de fin.");
    setBusy(true);
    try {
      const payload = { new_end: newEnd, keep_start: keepStart };
      if (irlValid) payload.new_rent = revisedRent;
      const res = await api.post(`/leases/${lease.id}/renew`, payload);
      onDone(res.data);
      toast.success("Bail renouvelé");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="renew-modal">
        <div className="flex items-center justify-between p-6 border-b border-brand-soft sticky top-0 bg-white">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep flex items-center gap-2"><RefreshCw className="w-5 h-5" /> Assistant de renouvellement</h3>
          <button onClick={onClose} className="text-brand-green/50 hover:text-brand-green"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="rounded-xl bg-brand-softer p-4 text-sm space-y-1">
            <p><span className="text-brand-green/50">Bien :</span> <b>{lease.property}</b></p>
            <p><span className="text-brand-green/50">Locataire :</span> {lease.tenant || "—"}</p>
            <p><span className="text-brand-green/50">Échéance actuelle :</span> {lease.end ? new Date(lease.end).toLocaleDateString("fr-FR") : "—"} · Loyer HC {rent.toLocaleString("fr-FR")} €</p>
          </div>

          {history.length > 0 && (
            <div data-testid="rent-history">
              <h4 className="font-semibold text-brand-greenDeep mb-2 flex items-center gap-2"><Repeat className="w-4 h-4" /> Historique des loyers</h4>
              <div className="space-y-1.5">
                {history.slice().reverse().map((h, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg bg-brand-softer">
                    <span className="text-brand-green/60">{new Date(h.date).toLocaleDateString("fr-FR")} · {h.type}{h.old_irl ? ` (IRL ${h.old_irl}→${h.new_irl})` : ""}</span>
                    <span className="font-medium text-brand-greenDeep flex items-center gap-2">{(h.old_rent || 0).toLocaleString("fr-FR")} € → <b>{(h.new_rent || 0).toLocaleString("fr-FR")} €</b>
                      {h.old_irl && i === history.length - 1 - history.map((x) => !!x.old_irl).lastIndexOf(true) && <button type="button" onClick={() => window.open(`${BACKEND_URL}/api/leases/${lease.id}/revision-letter-pdf`, "_blank")} className="text-[11px] px-2 py-0.5 rounded-full border border-brand-soft hover:bg-white" data-testid="revision-letter-btn">Courrier</button>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="font-semibold text-brand-greenDeep mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Nouvelle échéance</h4>
            <label className="block text-sm font-medium text-brand-green/70 mb-1.5">Nouvelle date de fin</label>
            <input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} className="input" data-testid="renew-new-end" />
            <button type="button" onClick={() => setKeepStart(!keepStart)} className={`w-full mt-3 flex items-center justify-between px-4 py-2.5 rounded-xl border transition-colors ${!keepStart ? "bg-brand-lime/20 border-brand-limeDark" : "border-brand-soft"}`} data-testid="renew-keepstart-toggle">
              <span className="text-sm text-left text-brand-green/80">Faire repartir le bail à la date de fin actuelle</span>
              <span className={`w-11 h-6 rounded-full relative transition-colors ${!keepStart ? "bg-brand-limeDark" : "bg-brand-soft"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${!keepStart ? "left-[22px]" : "left-0.5"}`} /></span>
            </button>
          </div>

          <div>
            <h4 className="font-semibold text-brand-greenDeep mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Révision du loyer ({idxKind})
              {irlMeta && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-lime/30 text-brand-greenDeep font-semibold" data-testid="irl-auto-badge">{idxKind} auto · INSEE</span>}
              <button type="button" onClick={refreshIrl} title="Actualiser depuis l'INSEE" className="ml-auto text-brand-green/40 hover:text-brand-greenDeep transition-colors" data-testid="irl-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></button>
            </h4>
            <p className="text-xs text-brand-green/50 mb-3">{irlMeta ? `Indices INSEE : ${irlMeta.year_ago.quarter} (${irlMeta.year_ago.value}) → ${irlMeta.latest.quarter} (${irlMeta.latest.value}) · source ${irlMeta.source || "INSEE"}. Ajustez si besoin.` : `Renseignez l'ancien et le nouvel indice ${idxKind}.`}</p>
            {irlMeta?.table?.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-2">
                <select value={irlMeta.table.find((t) => String(t.value) === oldIrl)?.quarter || ""} onChange={(e) => { const t = irlMeta.table.find((x) => x.quarter === e.target.value); if (t) setOldIrl(String(t.value)); }} className="input text-sm" data-testid="irl-old-quarter">
                  <option value="">Trimestre…</option>
                  <IrlOptions table={irlMeta.table} />
                </select>
                <select value={irlMeta.table.find((t) => String(t.value) === newIrl)?.quarter || ""} onChange={(e) => { const t = irlMeta.table.find((x) => x.quarter === e.target.value); if (t) setNewIrl(String(t.value)); }} className="input text-sm" data-testid="irl-new-quarter">
                  <option value="">Trimestre…</option>
                  <IrlOptions table={irlMeta.table} />
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Ancien {idxKind}</span><input type="number" step="0.01" value={oldIrl} onChange={(e) => setOldIrl(e.target.value)} placeholder="ex. 143.46" className="input" data-testid="irl-old-input" /></label>
              <label className="block"><span className="block text-sm font-medium text-brand-green/70 mb-1.5">Nouvel {idxKind}</span><input type="number" step="0.01" value={newIrl} onChange={(e) => setNewIrl(e.target.value)} placeholder="ex. 145.17" className="input" data-testid="irl-new-input" /></label>
            </div>
            {irlValid && (
              <div className="mt-3 rounded-xl bg-brand-lime/15 border border-brand-lime p-3 text-sm flex items-center justify-between" data-testid="irl-preview">
                <span className="text-brand-green/70">Nouveau loyer HC (+{increasePct}%)</span>
                <span className="font-bold text-brand-greenDeep">{revisedRent.toLocaleString("fr-FR")} €</span>
              </div>
            )}
            <button onClick={applyIrl} disabled={!irlValid || busy} className="mt-3 w-full py-2.5 rounded-full border border-brand-limeDark text-brand-greenDeep font-semibold text-sm hover:bg-brand-lime/20 transition-colors disabled:opacity-40" data-testid="apply-irl-btn">Appliquer la révision uniquement</button>
          </div>

          <button onClick={renew} disabled={busy} className="w-full py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="confirm-renew-btn">
            <RefreshCw className="w-4 h-4" /> Renouveler le bail{irlValid ? " avec révision" : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
